import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Mail, Phone, Tag, ArrowRight, Trash2, Loader2, Edit, X, Save, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import AppHeader from "../components/AppHeader";
import CRMTabs from "../components/CRMTabs";

const ETIQUETAS = ["Contacto", "Orientación", "Consultoría", "Informe Completo", "Gestión Post-Subasta"];
const ETIQUETA_COLOR = {
  "Contacto": "bg-stone-700 text-stone-200",
  "Orientación": "bg-blue-900/60 text-blue-200",
  "Consultoría": "bg-emerald-900/60 text-emerald-200",
  "Informe Completo": "bg-[#E0651F]/30 text-[#ff9655]",
  "Gestión Post-Subasta": "bg-purple-900/60 text-purple-200",
};

export default function CRMClientes() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [etiquetaFilter, setEtiquetaFilter] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);    // cliente object or { _new: true }

  const fetchClientes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (etiquetaFilter) params.set("etiqueta", etiquetaFilter);
      const { data } = await api.get(`/admin/crm/clientes?${params}`);
      setClientes(data.clientes || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClientes(); }, []);  // eslint-disable-line

  const onDelete = async (c) => {
    if (!window.confirm(`¿Eliminar a "${c.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/crm/clientes/${c.id}`);
      fetchClientes();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const onCrearTarjeta = async (c) => {
    try {
      const { data } = await api.post(`/admin/crm/clientes/${c.id}/crear-tarjeta`, {
        title: `${c.etiqueta} · ${c.nombre}`,
      });
      navigate(`/admin/crm/${data.id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error creando tarjeta");
    }
  };

  const counts = useMemo(() => {
    const c = { total: clientes.length };
    ETIQUETAS.forEach((e) => { c[e] = clientes.filter((cl) => cl.etiqueta === e).length; });
    return c;
  }, [clientes]);

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <AppHeader />

      <header className="px-4 sm:px-6 py-4 sm:py-5 bg-stone-900">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F]">CRM</div>
          <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-white">Base de datos de clientes</h1>
          <div className="text-xs sm:text-sm text-stone-400 mt-1">{counts.total} cliente(s) · todos los leads y contactos en un único sitio.</div>
        </div>
      </header>

      <CRMTabs />

      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-stone-900/40 border-b border-stone-800">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="relative flex-1 min-w-[180px] sm:flex-initial sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchClientes()}
              placeholder="Buscar nombre, email, NIF..."
              className="w-full bg-stone-800 border border-stone-700 text-stone-100 pl-9 pr-3 py-2 text-sm rounded-sm focus:border-[#E0651F] focus:outline-none"
              data-testid="clientes-search"
            />
          </div>
          <select
            value={etiquetaFilter}
            onChange={(e) => { setEtiquetaFilter(e.target.value); setTimeout(fetchClientes, 0); }}
            className="bg-stone-800 border border-stone-700 text-stone-100 px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-sm focus:outline-none max-w-[140px] sm:max-w-none"
            data-testid="clientes-etiqueta-filter"
          >
            <option value="">Todas las etiquetas</option>
            {ETIQUETAS.map((e) => <option key={e} value={e}>{e} ({counts[e] || 0})</option>)}
          </select>
          <button onClick={fetchClientes}
            className="bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-100 px-2.5 sm:px-3 py-2 text-[10px] sm:text-xs uppercase tracking-[0.15em] font-bold rounded-sm">
            Aplicar
          </button>
          <div className="hidden sm:block ml-auto" />
          <button
            onClick={() => setEditing({ _new: true })}
            className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-3 sm:px-4 py-2 text-[10px] sm:text-xs uppercase tracking-[0.15em] font-bold rounded-sm ml-auto sm:ml-0"
            data-testid="clientes-new-btn">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nuevo cliente</span><span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-200 border-y border-red-700 px-6 py-2 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#E0651F] mx-auto" /></div>
        ) : clientes.length === 0 ? (
          <div className="text-center py-20 text-stone-500">
            Sin clientes que mostrar.
            <div className="mt-3">
              <button onClick={() => setEditing({ _new: true })} className="text-[#E0651F] hover:underline text-sm">+ Crear el primero manualmente</button>
            </div>
          </div>
        ) : (
          <>
          {/* MOBILE: card view */}
          <div className="lg:hidden space-y-3" data-testid="clientes-cards-mobile">
            {clientes.map((c) => (
              <div key={c.id} className="bg-stone-900 border border-stone-800 rounded-sm p-4" data-testid={`cliente-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-white truncate">{c.nombre || "(sin nombre)"}</div>
                    {c.empresa && <div className="text-[11px] text-stone-500 truncate">{c.empresa}</div>}
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wide font-bold px-2 py-1 rounded-sm flex-shrink-0 ${ETIQUETA_COLOR[c.etiqueta] || "bg-stone-700 text-stone-200"}`}>
                    <Tag className="w-2.5 h-2.5" /> {c.etiqueta}
                  </span>
                </div>
                <div className="space-y-1 text-xs mb-3">
                  {c.email && <div className="flex items-center gap-1.5 text-stone-300 truncate"><Mail className="w-3 h-3 text-stone-500 flex-shrink-0" /> <span className="truncate">{c.email}</span></div>}
                  {c.telefono && <div className="flex items-center gap-1.5 text-stone-400"><Phone className="w-3 h-3 text-stone-500 flex-shrink-0" /> {c.telefono}</div>}
                  {c.nif && <div className="font-mono text-stone-400">{c.nif}</div>}
                  {c.pagos && c.pagos.length > 0 && (
                    <div className="text-emerald-400 text-[11px]">💶 {c.pagos.reduce((s, p) => s + (p.importe || 0), 0).toFixed(2)} € · {c.pagos.length} pago(s)</div>
                  )}
                  <div className="text-[10px] text-stone-500 capitalize">Origen: {(c.origen || "manual").replace(/_/g, " ")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onCrearTarjeta(c)} data-testid={`cliente-to-card-${c.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-2.5 py-2 text-[10px] uppercase tracking-wide font-bold rounded-sm">
                    <ArrowRight className="w-3 h-3" /> Tarjeta
                  </button>
                  <button onClick={() => setEditing(c)} data-testid={`cliente-edit-${c.id}`}
                    className="text-stone-400 hover:text-white p-2 border border-stone-800 rounded-sm">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(c)} data-testid={`cliente-delete-${c.id}`}
                    className="text-stone-500 hover:text-red-400 p-2 border border-stone-800 rounded-sm">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* DESKTOP: table */}
          <div className="hidden lg:block bg-stone-900 border border-stone-800 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-stone-800/60 text-stone-400 text-[10px] uppercase tracking-[0.15em] font-bold">
                <tr>
                  <th className="text-left px-4 py-3">Nombre</th>
                  <th className="text-left px-4 py-3">Etiqueta</th>
                  <th className="text-left px-4 py-3">Contacto</th>
                  <th className="text-left px-4 py-3">Facturación</th>
                  <th className="text-left px-4 py-3">Origen</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-800">
                {clientes.map((c) => (
                  <tr key={c.id} className="hover:bg-stone-800/40" data-testid={`cliente-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-bold text-white">{c.nombre || "(sin nombre)"}</div>
                      {c.empresa && <div className="text-[11px] text-stone-500">{c.empresa}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-sm ${ETIQUETA_COLOR[c.etiqueta] || "bg-stone-700 text-stone-200"}`}>
                        <Tag className="w-3 h-3" /> {c.etiqueta}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {c.email && <div className="flex items-center gap-1 text-stone-300"><Mail className="w-3 h-3 text-stone-500" /> {c.email}</div>}
                      {c.telefono && <div className="flex items-center gap-1 text-stone-400 text-xs mt-0.5"><Phone className="w-3 h-3 text-stone-500" /> {c.telefono}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {c.nif ? <div className="font-mono text-stone-300">{c.nif}</div> : <span className="text-stone-600">—</span>}
                      {c.direccion_facturacion && <div className="text-stone-500 truncate max-w-[200px]" title={c.direccion_facturacion}>{c.direccion_facturacion}</div>}
                      {c.pagos && c.pagos.length > 0 && (
                        <div className="text-emerald-400 text-[11px] mt-1">💶 {c.pagos.reduce((s, p) => s + (p.importe || 0), 0).toFixed(2)} € · {c.pagos.length} pago(s)</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-stone-500 capitalize">{(c.origen || "manual").replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => onCrearTarjeta(c)} title="Crear tarjeta de gestión"
                          data-testid={`cliente-to-card-desktop-${c.id}`}
                          className="inline-flex items-center gap-1 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-2.5 py-1.5 text-[10px] uppercase tracking-wide font-bold rounded-sm">
                          <ArrowRight className="w-3 h-3" /> Tarjeta
                        </button>
                        <button onClick={() => setEditing(c)} title="Editar"
                          data-testid={`cliente-edit-desktop-${c.id}`}
                          className="text-stone-400 hover:text-white p-1.5">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => onDelete(c)} title="Eliminar"
                          data-testid={`cliente-delete-desktop-${c.id}`}
                          className="text-stone-500 hover:text-red-400 p-1.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          </>
        )}
      </main>

      {editing && (
        <ClienteEditor cliente={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchClientes(); }} />
      )}
    </div>
  );
}

function ClienteEditor({ cliente, onClose, onSaved }) {
  const isNew = !!cliente._new;
  const [form, setForm] = useState({
    nombre: cliente.nombre || "",
    email: cliente.email || "",
    telefono: cliente.telefono || "",
    etiqueta: cliente.etiqueta || "Contacto",
    nif: cliente.nif || "",
    direccion_facturacion: cliente.direccion_facturacion || "",
    empresa: cliente.empresa || "",
    notas: cliente.notas || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.nombre || form.nombre.length < 2) {
      setErr("Introduce un nombre válido");
      return;
    }
    setSaving(true); setErr("");
    try {
      if (isNew) {
        await api.post("/admin/crm/clientes", form);
      } else {
        await api.patch(`/admin/crm/clientes/${cliente.id}`, form);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-900 border border-stone-700 rounded-sm w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-stone-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E0651F]">{isNew ? "Nuevo cliente" : "Editar cliente"}</div>
            <h2 className="font-display text-xl font-black text-white">{isNew ? "Datos del cliente" : cliente.nombre}</h2>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-white" data-testid="cliente-editor-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <Field label="Nombre completo *"><input value={form.nombre} onChange={set("nombre")} className="field" data-testid="cliente-field-nombre" autoFocus /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email"><input type="email" value={form.email} onChange={set("email")} className="field" data-testid="cliente-field-email" /></Field>
            <Field label="Teléfono"><input value={form.telefono} onChange={set("telefono")} className="field" data-testid="cliente-field-telefono" /></Field>
          </div>
          <Field label="Etiqueta *">
            <select value={form.etiqueta} onChange={set("etiqueta")} className="field" data-testid="cliente-field-etiqueta">
              {ETIQUETAS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="NIF / CIF"><input value={form.nif} onChange={set("nif")} className="field" data-testid="cliente-field-nif" /></Field>
            <Field label="Empresa"><input value={form.empresa} onChange={set("empresa")} className="field" data-testid="cliente-field-empresa" /></Field>
          </div>
          <Field label="Dirección de facturación"><input value={form.direccion_facturacion} onChange={set("direccion_facturacion")} className="field" data-testid="cliente-field-direccion" /></Field>
          <Field label="Notas"><textarea rows={3} value={form.notas} onChange={set("notas")} className="field resize-none" data-testid="cliente-field-notas" /></Field>

          {err && <div className="text-red-400 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {err}</div>}
        </div>

        <div className="px-6 py-4 border-t border-stone-800 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-stone-400 hover:text-white text-xs uppercase tracking-[0.15em] font-bold px-3 py-2">Cancelar</button>
          <button onClick={save} disabled={saving}
            data-testid="cliente-editor-save"
            className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Crear cliente" : "Guardar cambios"}
          </button>
        </div>

        <style>{`
          .field {
            width: 100%;
            background: #1c1917;
            border: 1px solid #44403c;
            color: #f5f5f4;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            border-radius: 2px;
            outline: none;
            transition: border-color .15s;
          }
          .field:focus { border-color: #E0651F; }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
